import { useEffect, useState } from 'react';
import { ApiFailure, api } from '../lib/api';

/**
 * 运营阈值。
 *
 * ★ 这几个数字原来写死在代码里 ——「派单后 30 分钟没接算异常」
 *   「建单后 4 小时没派算积压」「班次开始前 30 分钟可以签到」。
 *   它们是**运营的判断**,不是工程参数:每家店的服务标准不一样,
 *   而改一次不该发一次版。
 *
 * ★ 每一条都写清楚**调错了会怎样**。只写「派单超时分钟数」的话,
 *   运营无从判断该填多少;写「调太小会在司机正常操作的窗口里疯狂闪红」
 *   他就知道了。这和功能开关那一屏是同一套做法。
 */

interface Threshold {
  key: string;
  label: string;
  description: string;
  whenWrong: string;
  unit: string;
  default: number;
  min: number;
  max: number;
  value: number;
}

export function ThresholdSettings({ canManage }: { canManage: boolean }) {
  const [rows, setRows] = useState<Threshold[]>([]);
  const [draft, setDraft] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    void api<Threshold[]>('/settings/thresholds')
      .then((r) => {
        setRows(r);
        setDraft(Object.fromEntries(r.map((t) => [t.key, t.value])));
      })
      .catch((e) => setErr(e instanceof ApiFailure ? e.message : '加载失败'));
  }, []);

  async function save() {
    setBusy(true);
    setErr(null);
    setSaved(false);
    try {
      const r = await api<Threshold[]>('/settings/thresholds', {
        method: 'PATCH',
        body: { thresholds: draft },
      });
      setRows(r);
      setDraft(Object.fromEntries(r.map((t) => [t.key, t.value])));
      setSaved(true);
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '保存失败');
    } finally {
      setBusy(false);
    }
  }

  const dirty = rows.some((t) => draft[t.key] !== t.value);

  return (
    <section aria-label="运营阈值">
      <h2 className="mb-1 text-xl font-semibold text-slate-900 dark:text-slate-100">运营阈值</h2>
      <p className="mb-4 text-sm text-slate-500">
        这几个数字决定「什么时候算不对劲」。改完立刻生效,已经发生的事不受影响。
      </p>

      {err && (
        <div className="mb-3 max-w-2xl rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700
                        dark:bg-red-950 dark:text-red-400">
          {err}
        </div>
      )}

      <div className="max-w-2xl space-y-3">
        {rows.map((t) => (
          <div
            key={t.key}
            className="rounded-xl border border-slate-200 p-4 dark:border-slate-700"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="font-medium text-slate-900 dark:text-slate-100">{t.label}</div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  aria-label={t.label}
                  min={t.min}
                  max={t.max}
                  disabled={!canManage}
                  value={draft[t.key] ?? t.value}
                  onChange={(e) => setDraft({ ...draft, [t.key]: Number(e.target.value) })}
                  className="w-24 rounded-lg border border-slate-300 bg-white px-2 py-1.5
                             text-right tabular-nums dark:border-slate-600 dark:bg-slate-800"
                />
                <span className="text-sm text-slate-500">{t.unit}</span>
              </div>
            </div>

            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{t.description}</p>
            {/*
              ★ 「调错了会怎样」比标题重要 —— 见 shared/settings/thresholds 里的说明。
            */}
            <p className="mt-1 text-xs text-amber-700 dark:text-amber-500">⚠ {t.whenWrong}</p>
            <p className="mt-1 text-xs text-slate-400">
              可填 {t.min}–{t.max}{t.unit};默认 {t.default}
              {t.value !== t.default && `(现在是 ${t.value}${t.unit},已改过)`}
            </p>
          </div>
        ))}
      </div>

      {canManage && (
        <div className="mt-4 flex items-center gap-3">
          <button
            onClick={() => void save()}
            disabled={busy || !dirty}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white
                       disabled:opacity-40"
          >
            {busy ? '保存中…' : '保存'}
          </button>
          {saved && <span className="text-sm text-green-600 dark:text-green-400">已保存</span>}
        </div>
      )}
    </section>
  );
}
